(ns ncr.fragment.attachment-label
  (:require
   [clojure.core.match :refer [match]]
   [cljs.core.async :refer-macros [go] :refer [<!]]
   [ncr.widgets.core :as w]
   [ncr.etc.util :as u]
   [ncr.etc.pf :as pf]
   [ncr.etc.pubsub :as ps]
   [ncr.navigate :as n]
   [ncr.fragment.attachments-backend :as ba]
   [ncr.stories-util :as stories]
   [ncr.fragment.common :as fc]
   [uix.core.alpha :as uix]
   [ncr.fragment-types.widgets :as ftw]
   [ncr.fragment.ai-assist :as assist]
   [ncr.amplitude :as amp]))

(def content-types
  {"application/pdf"
   {:icon :file-pdf-outline}

   "application/msword"
   {:icon :file-word-outline}

   "application/ms-excel"
   {:icon :file-excel-outline}

   "application/ms-powerpoint"
   {:icon :file-powerpoint-outline}})

(defn content-type-info [{:keys [mimeType]}]
  (get content-types mimeType
    {:icon :file-outline}))

(defn- seconds-elapsed [start end]
  (-> (.getTime end)
    (- (.getTime start))
    (/ 1000)
    js/Math.round))

(defn- c-ocr-text-count [started-at]
  (let
    [*refresh-count (uix/state 0)
     refresh-count @*refresh-count]

    (uix/with-effect [refresh-count]
      (let [interval-id (js/setInterval #(swap! *refresh-count inc) 1000)]
        #(some-> interval-id js/clearInterval)))
    (str "OCR enqueued " (seconds-elapsed (js/Date. started-at) (js/Date.)) " seconds ago")))

(defn- c-ocr-text [{:keys [status started-at ended-at]}]
  (cond
    (and started-at (nil? status))
    [c-ocr-text-count started-at]

    (= :ok status)
    (str "OCR completed"
      (when (and started-at ended-at)
        (str " "
          (u/date-distance ended-at)
          " in " (seconds-elapsed (js/Date. started-at) (js/Date. ended-at)) "s")))

    (nil? status)
    "No OCR data"

    :else
    (str "OCR Failed (" (pr-str status) ")")))

(defn c-ocr-icon [{:keys [show-tooltip?]} {:keys [status started-at] :as meta}]
  (letfn
    [(tooltip [body]
       (if show-tooltip?
         [pf/c-tooltip
          {:position :right
           :content [c-ocr-text meta]}
          body]
         body))]
    (match
      (cond
        (and started-at (= :progress status))
        {:in-progress? true}

        (= :ok status)
        {:class :pf-u-success-color-100
         :icon :file-document-check-outline}

        :else
        {:class :pf-u-danger-color-100
         :icon :file-outline})

      {:in-progress? true}
      (tooltip
        [pf/c-spinner {:size :sm}])

      {:icon icon :class class}
      (tooltip
        [w/c-mdi-icon
         {:class [:ncr-c-attachment__icon class]
          :icon icon}]))))

(defn c-datev-text [{:keys [status ended-at started-at]}]
  (case status
    :error
    "Upload to DATEV failed"
    :progress
    "DATEV upload pending"
    :ok
    (str "Upload to DATEV "
      (if ended-at
        (str "completed " (u/date-distance ended-at))
        (str "enqueued " (u/date-distance started-at))))
    (str "Unknown DATEV status: " (pr-str status))))

(defn c-datev-icon [{:keys [show-tooltip?]} {:keys [status] :as aux}]
  (let
    [props'
     (case status
       :error
       {:icon :cloud-off-outline
        :class :pf-u-danger-color-100}
       :progress
       {:icon :cloud-sync-outline
        :class :pf-u-warning-color-200}
       :ok
       {:icon :cloud-check-outline
        :class :pf-u-success-color-100}
       {:icon :cloud-off-outline
        :class :pf-u-danger-color-100})
     body
     [w/c-mdi-icon
      (w/merge-props
        {:class [:ncr-c-attachment__icon]} props')]]
    (if show-tooltip?
      [pf/c-tooltip
       {:position :right
        :content [c-datev-text aux]}
       body]
      body)))

(defn c-assist-icon [{:keys [show-tooltip?]} {:keys [data reference] :as att}]
  (let [{:keys [suggestions status]} data]
    (case status
      :ok
      (when (seq suggestions)
        (cond->>
          [w/c-mdi-icon
           {:class [:ncr-c-attachment__icon :pf-u-ml-sm :pf-u-success-color-100]
            :title "AI Suggestions"
            :on-click u/stop-propagation
            :icon :wizard-hat}]

          show-tooltip?
          (conj [assist/c-assist-popover att])))

      :progress
      [pf/c-spinner
       {:size :sm :class :pf-u-ml-sm :title "AI Assist is running..."}]

      nil)))

(defn- c-attachment-tooltip [{:keys [fileName metadata auxdata]}]
  [pf/l-flex
   {:direction :column
    :align-items :alignItemsFlexStart
    :space-items :spaceItemsNone}
   (when-let [metadata (:textract metadata)]
     [pf/l-flex-item
      [c-ocr-icon {:show-tooltip? false} metadata]
      [:small.pf-u-pl-xs [c-ocr-text metadata]]])

   (when-let [auxdata (:datev metadata)]
     [pf/l-flex-item
      [c-datev-icon {:show-tooltip? false} auxdata]
      [:small.pf-u-pl-xs [c-datev-text auxdata]]])
   [pf/l-flex-item [:b fileName]]])

(defn- attachment-icons [{:keys [show-tooltip? icons]} {:keys [metadata auxdata reference] :as attachment}]
  (let [{:keys [datev assist]} auxdata
        {:keys [textract]} metadata
        props {:show-tooltip? (not show-tooltip?)}]
    (filter identity
      [(when (and datev (contains? icons :datev))
         [c-datev-icon props datev])
       (when (and textract (contains? icons :textract))
         [c-ocr-icon props textract])
       (when (and assist (contains? icons :assist))
         [c-assist-icon props {:reference reference :data assist}])])))

(defn c-attachment-icons [props attachment]
  (when-let [icons (seq (attachment-icons props attachment))]
    (into [pf/l-flex {:class-name :ncr-c-attachment-icons :space-items :space-items-none}]
      (map
        (fn [icon]
          (conj
            [pf/l-flex-item {:class-name :ncr-c-attachment-icons__icon}] icon))
        icons))))

(if stories/STORYBOOK
  (defn- get-href [_nav-props])
  (defn- get-href [nav-props]
    (n/fragment-href nav-props)))

(defn c-attachment-label [{:keys [open-in-new-tab? show-tooltip?] :as props} fragment {:keys [fileName metadata] :as attachment}]
  (let
    [ref (uix/ref)
     label-id (uix/memo #(gensym) [])

     reference-id (:id fragment)
     attachment (assoc attachment :reference reference-id)
     attachment-id (:id attachment)

     nav-props
     {:id reference-id
      :slug (fc/fragment-name->slug (:name fragment))
      :attachment-id attachment-id}

     href (get-href nav-props)
     track-base {"Origin" "Search" "Destination" "Attachment"}] ; for the moment c-attachement-label is only used in [c-search-item]

    ; this is a workaround for context-menu requires ref but pf/c-label doesn't support passing ref param
    (uix/layout-effect!
      (fn []
        (when-let [el (.getElementById js/document label-id)]
          (reset! ref el)))
      [attachment-id])

    [w/c-context-menu
     {:class :ncr-c-attachment-label
      :reference ref
      :menu
      (fn [close!]
        [pf/c-menu {:is-plain true}
         [pf/c-menu-item
          {:icon [w/c-menu-icon {:icon :eye-outline}]
           :to href
           :on-click
           (u/isolate*
             (fn [_]
               (amp/navigate! track-base)
               (n/push-fragment! nav-props)
               (close!)))}
          "View"]

         [pf/c-divider]

         [pf/c-menu-item
          {:icon [w/c-menu-icon {:icon :folder-eye-outline}]
           :to href
           :on-click
           (u/isolate*
             (fn [_]
               (ba/download! reference-id attachment-id :INLINE)
               (close!)))}
          "Open in new tab"]

         [pf/c-menu-item
          {:icon [w/c-menu-icon {:icon :printer-outline}]
           :to href
           :on-click
           (u/isolate*
             (fn [_e]
               (ba/print! reference-id attachment-id)
               (close!)))}
          "Print"]

         [pf/c-menu-item
          {:icon [w/c-menu-icon {:icon :file-download-outline}]
           :to href
           :on-click
           (u/isolate*
             (fn [_e]
               (ba/download! reference-id attachment-id :ATTACHMENT)
               (close!)))}
          "Download"]

         [pf/c-divider]

         [pf/c-menu-item
          {:icon [w/c-menu-icon {:icon :pencil-outline}]
           :is-disabled (not (:ownerWritableHint fragment))
           :on-click
           (u/isolate*
             (fn [_e]
               (go
                 (u/when-ok [_ (<! (ftw/!attachment-rename reference-id attachment (:name fragment)))]
                   (ps/pub! [:search/execute false])))
               (close!)))}
          "Rename"]

         [pf/c-menu-item
          {:icon [w/c-menu-icon {:icon :delete-outline}]
           :is-disabled (not (:ownerWritableHint fragment))
           :on-click
           (u/isolate*
             (fn [_]
               (go
                 (u/when-ok [_ (<! (ftw/!attachment-set-deleted reference-id [attachment] true))]
                   (ps/pub! [:search/execute false])))
               (close!)))}
          "Delete"]])}

     [pf/c-tooltip
      {:class :ncr-c-attachment-label__tooltip
       :position :right
       :trigger (if show-tooltip? :mouseenter :manual)
       :content
       [c-attachment-tooltip attachment]}

      [pf/c-label
       (w/merge-props
         {:id label-id
          :href href
          :color :blue
          :class :ncr-c-attachment-label
          :icon (c-attachment-icons props attachment)
          :on-click
          (fn [e]
            (.preventDefault e)
            (u/on-click-modifiers e
              {:on-ctrl-click
               (fn [event]
                 (ba/download! reference-id attachment-id :ATTACHMENT)
                 (.stopPropagation event))

               :on-click
               (fn [_]
                 (if open-in-new-tab?
                   (do
                     (amp/navigate! (merge track-base {"Target" "Blank"}))
                     (u/open-link! href))
                   (do
                     (amp/navigate! track-base)
                     (n/push-fragment! nav-props))))}))}
         (dissoc props :open-in-new-tab? :icons :show-tooltip?))
       [pf/c-truncate
        {:position :middle
         :tooltip-position :bottom-end
         :trailing-num-chars 7 ; see comment in `attachment_label.scss` before changing
         :content fileName}]]]]))
